/**
 * Página de Planilha de Dados
 * Visualiza e permite download de planilha com dados de unidades e documentos
 */
import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Download,
  RefreshCw,
  FileSpreadsheet,
  CheckCircle,
  AlertTriangle,
  XCircle,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Eye,
  Search,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogClose,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/lib/api';

const API_BASE = `${API_BASE_URL}/claro/api`;

interface UnitDocument {
  ordem: string;
  regional: string;
  uso: string;
  subTipo: string;
  tipoImovel: string;
  endereco: string;
  cep: string;
  cidade: string;
  situacaoUnidade: string;
  tipoDocumento: string;
  statusDocumento: string;
  dataVencimento: string;
  linkDownload: string;
}

interface SpreadsheetData {
  resumo: {
    dataExtracao: string;
    duracao: number;
    totalUnidades: number;
    totalDocumentos: number;
    linhasExportadas: number;
    documentosSemUnidade: number;
    comPdf: number;
    distribuicao: {
      regular: { qtd: number; pct: string };
      parcialmente: { qtd: number; pct: string };
      irregular: { qtd: number; pct: string };
      naoConfigurada: { qtd: number; pct: string };
    };
  };
  regular: UnitDocument[];
  parcialmente: UnitDocument[];
  irregular: UnitDocument[];
  naoConfigurada: UnitDocument[];
}

type TabKey = 'resumo' | 'regular' | 'parcialmente' | 'irregular' | 'naoConfigurada';

const TABS: { key: TabKey; label: string; icon: typeof CheckCircle; color: string }[] = [
  { key: 'resumo', label: 'Resumo', icon: FileSpreadsheet, color: 'text-blue-500' },
  { key: 'regular', label: 'Regular', icon: CheckCircle, color: 'text-green-500' },
  { key: 'parcialmente', label: 'Parcialmente', icon: AlertTriangle, color: 'text-orange-500' },
  { key: 'irregular', label: 'Irregular', icon: XCircle, color: 'text-red-500' },
  { key: 'naoConfigurada', label: 'Não configurada', icon: HelpCircle, color: 'text-gray-500' },
];

const DATA_HEADERS = [
  'Ordem (identificação)',
  'Regional',
  'Uso (imóvel)',
  'Sub tipo (imovel)',
  'Tipo do Imóvel',
  'Endereço',
  'CEP (unidade)',
  'Cidade (Documento)',
  'Situação da Unidade',
  'Tipo de documento',
  'Status (documentos)',
  'Data de vencimento',
  'Link para download',
];

async function fetchSpreadsheetData(): Promise<SpreadsheetData> {
  const res = await fetch(`${API_BASE}/spreadsheet/data`);
  if (!res.ok) throw new Error('Erro ao buscar dados da planilha');
  const json = await res.json();
  return json.result;
}

function ResumoTab({ resumo }: { resumo: SpreadsheetData['resumo'] }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold mb-4">Resumo da Extração</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="text-2xl font-bold">{resumo.totalUnidades.toLocaleString('pt-BR')}</div>
              <div className="text-sm text-muted-foreground">Total de Unidades</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="text-2xl font-bold">{resumo.totalDocumentos.toLocaleString('pt-BR')}</div>
              <div className="text-sm text-muted-foreground">Total de Documentos</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="text-2xl font-bold">{resumo.linhasExportadas.toLocaleString('pt-BR')}</div>
              <div className="text-sm text-muted-foreground">Linhas Exportadas</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="text-2xl font-bold">{resumo.comPdf.toLocaleString('pt-BR')}</div>
              <div className="text-sm text-muted-foreground">Com PDF</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold mb-4">Distribuição por Status</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="font-medium">Regular</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-bold">{resumo.distribuicao.regular.qtd.toLocaleString('pt-BR')}</span>
              <span className="text-muted-foreground w-16 text-right">{resumo.distribuicao.regular.pct}</span>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-orange-500/10 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              <span className="font-medium">Parcialmente</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-bold">{resumo.distribuicao.parcialmente.qtd.toLocaleString('pt-BR')}</span>
              <span className="text-muted-foreground w-16 text-right">{resumo.distribuicao.parcialmente.pct}</span>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <span className="font-medium">Irregular</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-bold">{resumo.distribuicao.irregular.qtd.toLocaleString('pt-BR')}</span>
              <span className="text-muted-foreground w-16 text-right">{resumo.distribuicao.irregular.pct}</span>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-500/10 rounded-lg">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-gray-500" />
              <span className="font-medium">Não configurada</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-bold">{resumo.distribuicao.naoConfigurada.qtd.toLocaleString('pt-BR')}</span>
              <span className="text-muted-foreground w-16 text-right">{resumo.distribuicao.naoConfigurada.pct}</span>
            </div>
          </div>
        </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold mb-4">Informações da Extração</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Data da extração:</span>
            <span className="ml-2 font-medium">{resumo.dataExtracao}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Duração:</span>
            <span className="ml-2 font-medium">{resumo.duracao.toFixed(2)}s</span>
          </div>
        </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold mb-4">Legenda de Status</h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
            <div>
              <span className="font-medium">Regular:</span>
              <span className="ml-1 text-muted-foreground">Unidade totalmente regular conforme parâmetros configurados</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5" />
            <div>
              <span className="font-medium">Parcialmente regular:</span>
              <span className="ml-1 text-muted-foreground">Alguns requisitos pendentes / parcialmente cumpridos</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <XCircle className="h-4 w-4 text-red-500 mt-0.5" />
            <div>
              <span className="font-medium">Irregular:</span>
              <span className="ml-1 text-muted-foreground">Requer atenção imediata (documentos críticos ausentes ou vencidos)</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <HelpCircle className="h-4 w-4 text-gray-500 mt-0.5" />
            <div>
              <span className="font-medium">Não configurada:</span>
              <span className="ml-1 text-muted-foreground">Status não definido ou não mapeado</span>
            </div>
          </div>
        </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DataTable({ data, maxRows = 100 }: { data: UnitDocument[]; maxRows?: number }) {
  const [expanded, setExpanded] = useState(false);
  const displayData = expanded ? data : data.slice(0, maxRows);

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        Mostrando {displayData.length.toLocaleString('pt-BR')} de {data.length.toLocaleString('pt-BR')} registros
      </div>
      
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              {DATA_HEADERS.map((header, idx) => (
                <th key={idx} className="text-left py-2 px-3 font-medium whitespace-nowrap">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.map((row, idx) => (
              <tr key={idx} className="border-t hover:bg-muted/30">
                <td className="py-2 px-3 whitespace-nowrap">{row.ordem}</td>
                <td className="py-2 px-3 whitespace-nowrap">{row.regional}</td>
                <td className="py-2 px-3 whitespace-nowrap">{row.uso}</td>
                <td className="py-2 px-3 whitespace-nowrap">{row.subTipo}</td>
                <td className="py-2 px-3 whitespace-nowrap">{row.tipoImovel}</td>
                <td className="py-2 px-3 max-w-[300px] truncate" title={row.endereco}>{row.endereco}</td>
                <td className="py-2 px-3 whitespace-nowrap">{row.cep}</td>
                <td className="py-2 px-3 whitespace-nowrap">{row.cidade}</td>
                <td className="py-2 px-3 whitespace-nowrap">{row.situacaoUnidade}</td>
                <td className="py-2 px-3 whitespace-nowrap">{row.tipoDocumento}</td>
                <td className="py-2 px-3 whitespace-nowrap">{row.statusDocumento}</td>
                <td className="py-2 px-3 whitespace-nowrap">{row.dataVencimento}</td>
                <td className="py-2 px-3">
                  {row.linkDownload && (
                    <a
                      href={row.linkDownload}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      Ver PDF
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </CardContent>
      </Card>

      {data.length > maxRows && (
        <Button
          variant="outline"
          onClick={() => setExpanded(!expanded)}
          className="w-full"
        >
          {expanded ? (
            <>
              <ChevronDown className="h-4 w-4 mr-2" />
              Mostrar menos
            </>
          ) : (
            <>
              <ChevronRight className="h-4 w-4 mr-2" />
              Mostrar todos os {data.length.toLocaleString('pt-BR')} registros
            </>
          )}
        </Button>
      )}
    </div>
  );
}

// ========== Sheet tab config for preview ==========
type SheetKey = 'resumo' | 'regular' | 'parcialmente' | 'irregular' | 'naoConfigurada';

const SHEET_TABS: { key: SheetKey; label: string; color: string; bg: string }[] = [
  { key: 'resumo', label: '📊 Resumo', color: 'text-blue-700', bg: 'bg-blue-100 border-blue-400' },
  { key: 'regular', label: '✅ Regular', color: 'text-green-700', bg: 'bg-green-100 border-green-400' },
  { key: 'parcialmente', label: '🟠 Parcialmente', color: 'text-orange-700', bg: 'bg-orange-100 border-orange-400' },
  { key: 'irregular', label: '❌ Irregular', color: 'text-red-700', bg: 'bg-red-100 border-red-400' },
  { key: 'naoConfigurada', label: '⚪ Não configurada', color: 'text-gray-700', bg: 'bg-gray-100 border-gray-400' },
];

const EXCEL_HEADERS = [
  'Ordem (identificação)',
  'Regional',
  'Uso (imóvel)',
  'Sub tipo (imovel)',
  'Tipo do Imóvel',
  'Endereço',
  'CEP (unidade)',
  'Cidade (Documento)',
  'Situação da Unidade',
  'Tipo de documento',
  'Status (documentos)',
  'Data de vencimento',
  'Link para download',
];

const COL_LETTERS = ['A','B','C','D','E','F','G','H','I','J','K','L','M'];

function SpreadsheetPreviewDialog({
  open,
  onOpenChange,
  data,
  onDownload,
  isDownloading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: SpreadsheetData;
  onDownload: () => void;
  isDownloading: boolean;
}) {
  const [activeSheet, setActiveSheet] = useState<SheetKey>('resumo');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 200;

  const sheetData = useMemo(() => {
    switch (activeSheet) {
      case 'regular': return data.regular;
      case 'parcialmente': return data.parcialmente;
      case 'irregular': return data.irregular;
      case 'naoConfigurada': return data.naoConfigurada;
      default: return [];
    }
  }, [activeSheet, data]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return sheetData;
    const term = searchTerm.toLowerCase();
    return sheetData.filter((row) =>
      Object.values(row).some((v) => String(v).toLowerCase().includes(term))
    );
  }, [sheetData, searchTerm]);

  const paginatedData = useMemo(() => {
    return filteredData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  }, [filteredData, page]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);

  const handleSheetChange = useCallback((key: SheetKey) => {
    setActiveSheet(key);
    setSearchTerm('');
    setPage(0);
  }, []);

  const getSheetCount = (key: SheetKey) => {
    switch (key) {
      case 'regular': return data.regular.length;
      case 'parcialmente': return data.parcialmente.length;
      case 'irregular': return data.irregular.length;
      case 'naoConfigurada': return data.naoConfigurada.length;
      default: return 0;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[98vw] w-[98vw] max-h-[95vh] h-[95vh] p-0 gap-0 flex flex-col overflow-hidden [&>button:last-child]:hidden">
        {/* Excel-style toolbar */}
        <div className="flex items-center justify-between px-3 py-2 bg-[#217346] text-white shrink-0">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-5 w-5" />
            <span className="font-medium text-sm">
              planilha_units_documentos_{new Date().toISOString().slice(0, 10)}.xlsx
            </span>
            <Badge variant="secondary" className="bg-white/20 text-white text-xs hover:bg-white/30">
              Pré-visualização
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={onDownload}
              disabled={isDownloading}
              className="gap-1.5 bg-white/20 hover:bg-white/30 text-white border-0 h-8"
            >
              <Download className="h-3.5 w-3.5" />
              {isDownloading ? 'Baixando...' : 'Baixar'}
            </Button>
            <DialogClose asChild>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 h-8 w-8 p-0">
                <X className="h-4 w-4" />
              </Button>
            </DialogClose>
          </div>
        </div>

        {/* Formula bar / Search */}
        {activeSheet !== 'resumo' && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-[#f3f3f3] dark:bg-zinc-800 border-b shrink-0">
            <div className="flex items-center gap-1 bg-white dark:bg-zinc-900 border rounded px-2 py-1 flex-1 max-w-md">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                placeholder="Buscar na planilha..."
                className="text-sm bg-transparent border-0 outline-none flex-1 min-w-0"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {filteredData.length.toLocaleString('pt-BR')} registros
              {searchTerm && ` (filtrados de ${sheetData.length.toLocaleString('pt-BR')})`}
            </span>
          </div>
        )}

        {/* Spreadsheet body */}
        <div className="flex-1 overflow-auto bg-white dark:bg-zinc-950">
          {activeSheet === 'resumo' ? (
            <ResumoPreview resumo={data.resumo} />
          ) : (
            <table className="w-full border-collapse text-xs">
              {/* Column letters */}
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#f3f3f3] dark:bg-zinc-800 border-b">
                  <th className="w-12 min-w-[48px] border-r border-b bg-[#e8e8e8] dark:bg-zinc-700 text-center text-[10px] text-muted-foreground py-0.5"></th>
                  {COL_LETTERS.map((letter, i) => (
                    <th key={letter} className="border-r border-b bg-[#e8e8e8] dark:bg-zinc-700 text-center text-[10px] text-muted-foreground py-0.5 min-w-[80px]">
                      {letter}
                    </th>
                  ))}
                </tr>
                {/* Headers row */}
                <tr className="bg-[#4472C4] text-white">
                  <th className="w-12 min-w-[48px] border-r border-b bg-[#365FA0] text-center text-[10px] py-1.5 font-medium">1</th>
                  {EXCEL_HEADERS.map((header, i) => (
                    <th key={i} className="border-r border-b px-2 py-1.5 text-left font-semibold whitespace-nowrap text-[11px]">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, idx) => {
                  const rowNum = page * PAGE_SIZE + idx + 2;
                  const isEven = idx % 2 === 0;
                  return (
                    <tr
                      key={idx}
                      className={cn(
                        'hover:bg-blue-50 dark:hover:bg-blue-950/30 border-b',
                        isEven ? 'bg-[#D9E2F3] dark:bg-zinc-900/50' : 'bg-white dark:bg-zinc-950'
                      )}
                    >
                      <td className="w-12 min-w-[48px] border-r bg-[#e8e8e8] dark:bg-zinc-700 text-center text-[10px] text-muted-foreground py-1 font-medium">
                        {rowNum}
                      </td>
                      <td className="border-r px-2 py-1 whitespace-nowrap">{row.ordem}</td>
                      <td className="border-r px-2 py-1 whitespace-nowrap">{row.regional}</td>
                      <td className="border-r px-2 py-1 whitespace-nowrap">{row.uso}</td>
                      <td className="border-r px-2 py-1 whitespace-nowrap">{row.subTipo}</td>
                      <td className="border-r px-2 py-1 whitespace-nowrap">{row.tipoImovel}</td>
                      <td className="border-r px-2 py-1 max-w-[350px] truncate" title={row.endereco}>{row.endereco}</td>
                      <td className="border-r px-2 py-1 whitespace-nowrap">{row.cep}</td>
                      <td className="border-r px-2 py-1 whitespace-nowrap">{row.cidade}</td>
                      <td className="border-r px-2 py-1 whitespace-nowrap">{row.situacaoUnidade}</td>
                      <td className="border-r px-2 py-1 whitespace-nowrap font-medium">{row.tipoDocumento}</td>
                      <td className="border-r px-2 py-1 whitespace-nowrap">
                        <span className={cn(
                          'inline-block px-1.5 py-0.5 rounded text-[10px] font-medium',
                          row.statusDocumento === 'Vigente' && 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                          row.statusDocumento === 'Vencido' && 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                          row.statusDocumento === 'Permanente' && 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                          row.statusDocumento === 'Isento' && 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
                          row.statusDocumento?.includes('renovação') && 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
                          row.statusDocumento?.includes('obtenção') && 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
                          row.statusDocumento === 'Inexistente' && 'bg-red-200 text-red-900 dark:bg-red-950 dark:text-red-200',
                        )}>
                          {row.statusDocumento}
                        </span>
                      </td>
                      <td className="border-r px-2 py-1 whitespace-nowrap">{row.dataVencimento ? new Date(row.dataVencimento).toLocaleDateString('pt-BR') : ''}</td>
                      <td className="px-2 py-1">
                        {row.linkDownload && (
                          <a href={row.linkDownload} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-[10px]">
                            PDF
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {activeSheet !== 'resumo' && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 px-3 py-1.5 bg-[#f3f3f3] dark:bg-zinc-800 border-t shrink-0">
            <Button
              size="sm"
              variant="ghost"
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
              className="h-7 text-xs"
            >
              ← Anterior
            </Button>
            <span className="text-xs text-muted-foreground">
              Página {page + 1} de {totalPages}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
              className="h-7 text-xs"
            >
              Próxima →
            </Button>
          </div>
        )}

        {/* Sheet tabs (Excel-like bottom tabs) */}
        <div className="flex items-center gap-0 border-t bg-[#e8e8e8] dark:bg-zinc-800 shrink-0 overflow-x-auto">
          {SHEET_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleSheetChange(tab.key)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium border-r whitespace-nowrap transition-colors',
                activeSheet === tab.key
                  ? `${tab.bg} border-t-2 ${tab.color}`
                  : 'bg-[#f3f3f3] dark:bg-zinc-700 text-muted-foreground hover:bg-white dark:hover:bg-zinc-600 border-t-2 border-t-transparent'
              )}
            >
              {tab.label}
              {tab.key !== 'resumo' && (
                <span className="ml-1.5 text-[10px] opacity-70">
                  ({getSheetCount(tab.key).toLocaleString('pt-BR')})
                </span>
              )}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResumoPreview({ resumo }: { resumo: SpreadsheetData['resumo'] }) {
  const rows: [string, string | number, string?][] = [
    ['📊 Resumo da Extração', '', ''],
    ['', '', ''],
    ['🗓 Data da extração', resumo.dataExtracao],
    ['⏱ Duração (s)', resumo.duracao],
    ['🏢 Total de unidades (JSON)', resumo.totalUnidades],
    ['📄 Total de documentos (JSON)', resumo.totalDocumentos],
    ['🔗 Linhas exportadas (joins)', resumo.linhasExportadas],
    ['⚠️ Documentos sem unidade', resumo.documentosSemUnidade],
    ['📑 Com PDF', resumo.comPdf],
    ['', '', ''],
    ['📌 Distribuição por Status', 'Qtd', '%'],
    ['✅ Regular', resumo.distribuicao.regular.qtd, resumo.distribuicao.regular.pct],
    ['🟠 Parcialmente', resumo.distribuicao.parcialmente.qtd, resumo.distribuicao.parcialmente.pct],
    ['❌ Irregular', resumo.distribuicao.irregular.qtd, resumo.distribuicao.irregular.pct],
    ['⚪ Não configurada', resumo.distribuicao.naoConfigurada.qtd, resumo.distribuicao.naoConfigurada.pct],
  ];
  return (
    <table className="border-collapse text-xs m-4">
      <thead>
        <tr className="bg-[#e8e8e8] dark:bg-zinc-700">
          <th className="w-10 border-r border-b text-center text-[10px] text-muted-foreground py-0.5"></th>
          <th className="border-r border-b text-center text-[10px] text-muted-foreground py-0.5 min-w-[300px]">A</th>
          <th className="border-r border-b text-center text-[10px] text-muted-foreground py-0.5 min-w-[200px]">B</th>
          <th className="border-r border-b text-center text-[10px] text-muted-foreground py-0.5 min-w-[80px]">C</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([a, b, c], idx) => (
          <tr key={idx} className="border-b hover:bg-blue-50 dark:hover:bg-blue-950/30">
            <td className="border-r bg-[#e8e8e8] dark:bg-zinc-700 text-center text-[10px] text-muted-foreground py-1 font-medium w-10">
              {idx + 1}
            </td>
            <td className={cn('border-r px-2 py-1', idx === 0 && 'font-bold text-sm')}>{a}</td>
            <td className="border-r px-2 py-1 text-right font-mono">{typeof b === 'number' ? b.toLocaleString('pt-BR') : b}</td>
            <td className="border-r px-2 py-1 text-right">{c ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AdminSpreadsheetPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('resumo');
  const [isDownloading, setIsDownloading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const { toast } = useToast();

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['spreadsheet-data'],
    queryFn: fetchSpreadsheetData,
  });

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await fetch(`${API_BASE}/spreadsheet/download`);
      if (!response.ok) throw new Error('Erro ao baixar arquivo');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `planilha_units_documentos_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erro ao baixar:', error);
      toast({ title: 'Erro ao baixar o arquivo', description: 'Tente novamente.', variant: 'destructive' });
    } finally {
      setIsDownloading(false);
    }
  };

  const getTabData = (key: TabKey): UnitDocument[] => {
    if (!data) return [];
    switch (key) {
      case 'regular':
        return data.regular;
      case 'parcialmente':
        return data.parcialmente;
      case 'irregular':
        return data.irregular;
      case 'naoConfigurada':
        return data.naoConfigurada;
      default:
        return [];
    }
  };

  const getTabCount = (key: TabKey): number => {
    if (!data) return 0;
    return getTabData(key).length;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Planilha de Dados</h2>
          <p className="text-muted-foreground">
            Visualize e exporte dados de unidades e documentos
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-2"
          >
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            Atualizar
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowPreview(true)}
            disabled={isLoading || !data}
            className="gap-2"
          >
            <Eye className="h-4 w-4" />
            Pré-visualizar
          </Button>
          <Button
            onClick={handleDownload}
            disabled={isDownloading || isLoading}
            className="gap-2"
          >
            <Download className={cn('h-4 w-4', isDownloading && 'animate-bounce')} />
            {isDownloading ? 'Baixando...' : 'Baixar Excel'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabKey)} className="space-y-4">
        <TabsList className="flex-wrap h-auto gap-1">
          {TABS.map((tab) => (
            <TabsTrigger key={tab.key} value={tab.key} className="gap-2">
              <tab.icon className={cn('h-4 w-4', tab.color)} />
              <span>{tab.label}</span>
              {tab.key !== 'resumo' && data && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {getTabCount(tab.key).toLocaleString('pt-BR')}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center gap-4">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="text-muted-foreground">Carregando dados...</span>
            </div>
          </div>
        ) : data ? (
          <>
            <TabsContent value="resumo">
              <ResumoTab resumo={data.resumo} />
            </TabsContent>
            {TABS.filter(t => t.key !== 'resumo').map(tab => (
              <TabsContent key={tab.key} value={tab.key}>
                <DataTable data={getTabData(tab.key)} />
              </TabsContent>
            ))}
          </>
        ) : (
          <div className="flex items-center justify-center min-h-[400px]">
            <span className="text-muted-foreground">Nenhum dado disponível</span>
          </div>
        )}
      </Tabs>

      {/* Preview Dialog */}
      {data && (
        <SpreadsheetPreviewDialog
          open={showPreview}
          onOpenChange={setShowPreview}
          data={data}
          onDownload={handleDownload}
          isDownloading={isDownloading}
        />
      )}
    </div>
  );
}
